/**
 * Wire-case conversion (ADR 0009, docs/plan/10-api-contract.md §1.2).
 *
 * The backend speaks `snake_case` for **every** JSON object key, query-string
 * parameter and multipart field name. `types.ts` stays `camelCase`. Rather
 * than maintaining a per-type mapper for each — which is precisely the drift
 * these ADRs exist to end — the transport converts *mechanically*, in one
 * place, on the way in and on the way out.
 *
 * ## What is and is not converted
 *
 * - **Object keys only.** String *values* are never re-cased: identifiers,
 *   cursors, email addresses, header names and the hyphenated enum spellings
 *   (`reauth-required`, `date-desc`, `this-and-future`) are data.
 * - **Plain objects and arrays only.** A `FormData`, `Blob`, `File`, `Date`,
 *   `Map`, `Set`, typed array or class instance is passed through by
 *   reference: it is not a JSON object, and walking it would corrupt it.
 * - **Total, not selective.** Every key of every plain object is converted,
 *   including keys this file has never heard of. A field the frontend does not
 *   model yet survives the round trip instead of being silently dropped.
 * - **The error envelope is excluded** (ADR 0009). `ApiError` parses
 *   `{error:{code,message,details,request_id}}` verbatim and reads raw
 *   `details` keys (`retry_after_sec`, `attempts_remaining`, `permanent`,
 *   `in_progress`). Structurally this is guaranteed because `http.ts` only
 *   calls `fromWire` on 2xx bodies; `fromWire` additionally short-circuits on
 *   anything shaped like the envelope, so the rule holds wherever it is called.
 *
 * ## Known limitation
 *
 * `camelToSnake` treats each capital as its own word boundary, so a key
 * spelled with an acronym run (`bodyHTML`) would round-trip to `bodyHtml`.
 * No key in `types.ts` is spelled that way, and §1.2 requires none: the
 * contract's names are all single-capital camelCase (`bodyHtml`, `authHost`,
 * `uidValidity`). Keys that are already lowercase, or that contain digits with
 * no underscore (`p256dh`), are fixed points of both directions.
 */

/** A JSON object that this module is allowed to walk into. */
type PlainObject = Record<string, unknown>;

/**
 * True only for `{}`-shaped objects.
 *
 * `Object.create(null)` counts (a JSON parse with a `__proto__` key can produce
 * one); a `Date`, `Blob`, `FormData` or class instance does not.
 */
function isPlainObject(value: unknown): value is PlainObject {
  if (typeof value !== 'object' || value === null) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** `bodyHtml` → `body_html`; already-snake and lowercase keys are unchanged. */
export function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** `body_html` → `bodyHtml`; `p256dh` and `email` are unchanged. */
export function snakeToCamel(key: string): string {
  return key.replace(/_+([a-z0-9])/g, (_match, char: string) => char.toUpperCase());
}

function convert(value: unknown, convertKey: (key: string) => string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => convert(item, convertKey));
  }
  if (!isPlainObject(value)) {
    // Primitives, null, undefined, FormData, Blob, File, Date, Map, Set,
    // class instances: handed back by reference, untouched.
    return value;
  }
  const out: PlainObject = {};
  for (const [key, member] of Object.entries(value)) {
    out[convertKey(key)] = convert(member, convertKey);
  }
  return out;
}

/**
 * True when `value` is the backend's error envelope.
 *
 * Used to keep `fromWire` off the one shape ADR 0009 excludes. It is a
 * structural test, not a list of fields, so an envelope carrying a `details`
 * key this build has never seen is still recognised and still left alone.
 */
export function isErrorEnvelope(value: unknown): boolean {
  return isPlainObject(value) && isPlainObject(value.error) && typeof value.error.code === 'string';
}

/**
 * Outgoing: `camelCase` keys → `snake_case`.
 *
 * Applied by `HttpTransport` to JSON request bodies and to query objects.
 * Multipart bodies are `FormData` and are returned by reference — the caller
 * spells those field names itself (§9.2).
 */
export function toWire<T>(value: T): unknown {
  return convert(value, camelToSnake);
}

/**
 * Incoming: `snake_case` keys → `camelCase`.
 *
 * Applied by `HttpTransport` to 2xx JSON response bodies only. Returns the
 * error envelope untouched so `ApiError` keeps reading `request_id` and raw
 * `details` keys.
 */
export function fromWire<T>(value: unknown): T {
  if (isErrorEnvelope(value)) return value as T;
  return convert(value, snakeToCamel) as T;
}
